import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { refreshSession } from '../services/api';
import { checkHasProfile } from '../services/studentProfile';
import { adminGetMyAccess } from '../services/adminAccess';
import {
  LandingHeader,
  HeroSection,
  WhatIsRudvanSection,
  FeaturesSection,
  HowItWorksSection,
  DemoSection,
  ComparisonSection,
  AudienceSection,
  PricingSection,
  TeamTeaserSection,
  LandingFooter,
  LoginModal,
} from '../components/Landing';

// Role-aware landing page after staff login (docs/DECISIONS.md, "Role-aware
// staff login landing page" decision) - Manager's default permission bundle
// deliberately also includes Mentor's/Support's view access (so Manager can
// monitor mentor/support activity, US-MGR-004/006), so this can't just check
// "does this one permission exist" per role in isolation - the checks below
// are ordered most-specific-bundle-first (`staff:view` only appears in
// Manager's default bundle among these three) so a Manager is never
// misrouted into Mentor's or Support's own landing page. Superadmin/Admin
// keeps today's default (`/admin`, the Configure Exam screen). The shared
// sidebar itself is unchanged - every role can still reach every workspace
// it has permission for; this only changes the FIRST screen after login.
async function resolveAdminLandingPath() {
  try {
    const access = await Promise.race([
      adminGetMyAccess(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('adminGetMyAccess timed out')), 5000)),
    ]);
    if (access.is_superadmin) return '/admin';
    const permissions = access.permissions || [];
    if (permissions.includes('staff:view')) return '/admin/manager';
    if (permissions.includes('support:view')) return '/admin/support';
    if (permissions.includes('student_profile:view')) return '/admin/mentor';
    return '/admin';
  } catch (error) {
    console.error('[Login] adminGetMyAccess failed or timed out, defaulting to /admin', error);
    return '/admin';
  }
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, tokenExpiresAt, login, logout, loading: authLoading } = useAuth();
  // Same staleness check as ProtectedRoute.jsx - a `user` left over in
  // localStorage with no token, or an expired one, is NOT "logged in".
  // Previously this page only checked `user` truthiness, so a stale session
  // would redirect straight back to e.g. /practice, ProtectedRoute would
  // correctly bounce it right back here (it does check the token), and
  // Login.jsx would redirect away again - an infinite /login <-> /practice
  // loop, reported as "the login page takes a long time to load" (it was
  // actually never settling at all - the browser's own navigation-
  // throttling protection was kicking in) (docs/DECISIONS.md, "Login
  // redirect loop for a stale/expired session").
  const tokenExpired = Boolean(tokenExpiresAt) && tokenExpiresAt <= Math.floor(Date.now() / 1000);
  const hasValidSession = Boolean(user) && Boolean(token) && !tokenExpired;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Landing page now defaults to light mode (brand guideline's "clean white
  // background" ask); toggle kept for users who prefer dark. Shared,
  // persisted theme state (docs/DECISIONS.md, "Persistent left-side student
  // navigation + light-theme visual fix") instead of a page-local toggle
  // that reset on every navigation.
  const { isDarkTheme, setIsDarkTheme } = useThemeMode();

  // ProtectedRoute stashes the originally-requested path (e.g. an exam link
  // opened while logged out) in location.state.from before redirecting here
  // - send the user back there after login instead of always landing on
  // /practice (docs/DECISIONS.md, 2026-07-29 "Login redirect target after
  // an exam-link login"; route renamed from /curriculum to /practice in
  // the "Bug-report batch..." follow-up). With no explicit "from", admins
  // land on /admin instead of /practice by default (docs/DECISIONS.md,
  // 2026-08-06 "admin default post-login destination") - `user.isAdmin`
  // comes straight back on the /api/auth/refresh response now (server
  // checks the same ADMIN_EMAILS allowlist admin_service uses), no
  // separate probe call needed (docs/DECISIONS.md, "student-facing UI
  // batch: curriculum rebrand + registration", login-speed fix - this
  // used to be a second, sequential round trip to a different service).
  //
  // Admin sub-pages (/admin/settings, /admin/profile) are reached only via
  // in-app navigation, never an emailed deep link like exam URLs are - so if
  // a session expired while on one of those and got stashed as "from", a
  // fresh login should land on the main console, not jump back into
  // Settings/Profile (docs/DECISIONS.md, 2026-08-07 "Login redirect:
  // admin sub-pages normalize to /admin").
  //
  // First-time (no student profile yet) students land on /register instead
  // of /practice (docs/DECISIONS.md, "student-facing UI batch: curriculum
  // rebrand + registration") - checked only for the no-explicitFrom, non-
  // admin case. An explicit `from` (e.g. an emailed exam link) is
  // deliberately NOT gated on registration - the assessment/test flow
  // (QuizPortal, admin-configured exams) is a separate concern from the
  // practice flow this registration personalizes, confirmed explicitly
  // during planning; a student clicking an exam link shouldn't be forced
  // through registration first.
  const rawFrom = location.state?.from;
  const explicitFrom = rawFrom && rawFrom.startsWith('/admin') && rawFrom !== '/admin' ? '/admin' : rawFrom;

  // Idle-time prefetch of the two most likely post-login destinations
  // (docs/DECISIONS.md, "Bug-report batch: session/exam-flow/notification/
  // curriculum fixes" - "optimize the UI for fast loading"). CurriculumMenuPage
  // is the /practice route's component (renamed from /curriculum) - App.js's
  // lazy(() => import('./pages/CurriculumMenuPage')) and its Registration
  // counterpart resolve to these exact same module files, so webpack
  // dedupes this to the same chunk request - by the time a student
  // actually logs in, the chunk is often already cached instead of only
  // starting to download after the profile-check round trip below
  // resolves. Runs while the student is still looking at the marketing
  // page, so it never competes with anything on the critical path.
  useEffect(() => {
    const prefetch = () => {
      import('./CurriculumMenuPage');
      import('./Registration');
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch);
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 2000);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!hasValidSession) {
      // Stale user, no valid token - clear it instead of redirecting away
      // (see hasValidSession's own comment above for why redirecting here
      // caused an infinite loop). Once logout() clears `user`, this
      // component just falls through to the normal render path below and
      // shows the landing page, exactly as if nothing had ever been
      // stored.
      logout();
      return;
    }
    if (explicitFrom) {
      navigate(explicitFrom, { replace: true });
      return;
    }
    if (user.isAdmin) {
      let cancelled = false;
      (async () => {
        const target = await resolveAdminLandingPath();
        if (!cancelled) {
          navigate(target, { replace: true });
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    // Parent Login (docs/DECISIONS.md, 2026-08-25 "Parent login..."
    // decision) - accountType comes back on the same /api/auth/refresh
    // response as isAdmin, same "single call decides the redirect" reason.
    // A parent has no student profile of their own, so this must return
    // before the checkHasProfile() flow below (which assumes the logged-in
    // identity IS a student).
    if (user.accountType === 'parent') {
      navigate('/parent/select-child', { replace: true });
      return;
    }

    // Same prefetch as above, fired eagerly here (not just on idle) since
    // an already-authenticated user reaching this effect is seconds (or
    // less) away from actually needing one of these chunks - this is the
    // exact path the reported "delay in loading" was hit on.
    import('./CurriculumMenuPage');
    import('./Registration');

    let cancelled = false;
    (async () => {
      // checkHasProfile() must never be allowed to block this redirect
      // indefinitely - a cold Lambda measured directly at ~7.5s (vs. ~0.9s
      // warm) already makes the wait feel broken, and an outright failure
      // (network error, backend 5xx) used to leave `user` truthy with no
      // navigate() ever firing at all - since the render below now shows
      // only a bare spinner while `user` is set (the earlier flicker fix),
      // that meant an indefinite, dead-looking spinner instead of the old
      // (still-imperfect) marketing-page flash (docs/DECISIONS.md,
      // "Bug-report batch: session/exam-flow/notification/curriculum
      // fixes" follow-up - "/login flickering not fixed... looks
      // something broken not loading"). /practice tolerates an unknown
      // profile fine (falls back to the unfiltered syllabus), so it's the
      // safe default on any failure/timeout here - a wrong guess here is
      // recoverable, an indefinite hang is not.
      let hasProfile = true;
      try {
        hasProfile = await Promise.race([
          checkHasProfile(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('checkHasProfile timed out')), 5000)),
        ]);
      } catch (error) {
        console.error('[Login] checkHasProfile failed or timed out, defaulting to /practice', error);
      }
      if (!cancelled) {
        navigate(hasProfile ? '/practice' : '/register', { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, user, explicitFrom, hasValidSession, logout]);

  const handleLoginSuccess = async (credentialResponse) => {
    setLoginError('');

    // Exchange the Google ID token for a server-issued session JWT
    // (server/index.js's /api/auth/refresh) so authenticated backend calls -
    // including the FR-8 Quiz Portal and FR-14 Admin Portal - have a real
    // Authorization: Bearer token to send, per docs/DECISIONS.md
    // (2026-07-28 "FR-8 Quiz Portal: auth architecture..."). No fallback to
    // a client-only session: that used to silently set `user` without ever
    // setting `authToken`, so every subsequent API call went out with no
    // Authorization header and failed later with a confusing "access
    // denied" instead of this clear message (docs/DECISIONS.md, 2026-07-29
    // "Login.jsx silent tokenless-session fallback"). Navigation itself
    // happens in the effect above once `user` is set - it also needs to
    // resolve the admin-vs-default destination, so there's a single place
    // that does it instead of duplicating the check here.
    try {
      const session = await refreshSession(credentialResponse.credential);
      login(session.user, { token: session.token, expiresAt: session.expiresAt });
    } catch (error) {
      console.error('Session exchange failed:', error);
      setLoginError("Couldn't sign you in - the authentication server is unreachable. Please try again shortly.");
    }
  };

  const handleLoginError = () => {
    console.log('Login failed');
  };

  const openLoginModal = () => setShowLoginModal(true);

  // While AuthContext is still restoring a persisted session from
  // localStorage, or once a user is already known (the effect above is
  // about to navigate them away), render a neutral loading state instead
  // of the full marketing landing page - otherwise an already-logged-in
  // user visiting "/" briefly saw the whole public landing page flash on
  // screen for one paint before the redirect effect fired (docs/
  // DECISIONS.md, "Bug-report batch: session/exam-flow/notification/
  // curriculum fixes").
  if (authLoading || user) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="inline-block animate-spin">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-blue rounded-full" />
          </div>
          {/* Reassurance text for the (bounded but occasionally multi-
              second, on a cold backend) user-path wait above - a bare
              spinner with nothing else on screen reads as "stuck"/broken
              rather than "working, just slow" (docs/DECISIONS.md,
              "Bug-report batch..." follow-up). */}
          {user && <p className="mt-4 text-sm text-text-slate">Signing you in...</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkTheme ? 'bg-navy' : 'bg-white'}`}>
      <LandingHeader
        isDarkTheme={isDarkTheme}
        onToggleTheme={() => setIsDarkTheme((prev) => !prev)}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen((prev) => !prev)}
        onLoginClick={openLoginModal}
      />

      <HeroSection isDarkTheme={isDarkTheme} onLoginClick={openLoginModal} />
      <WhatIsRudvanSection isDarkTheme={isDarkTheme} />
      <FeaturesSection isDarkTheme={isDarkTheme} />
      <HowItWorksSection isDarkTheme={isDarkTheme} onLoginClick={openLoginModal} />
      <DemoSection isDarkTheme={isDarkTheme} />
      <ComparisonSection isDarkTheme={isDarkTheme} />
      <AudienceSection isDarkTheme={isDarkTheme} />
      <PricingSection isDarkTheme={isDarkTheme} onLoginClick={openLoginModal} />
      <TeamTeaserSection isDarkTheme={isDarkTheme} />
      <LandingFooter />

      {showLoginModal && (
        <LoginModal
          isDarkTheme={isDarkTheme}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
          onLoginError={handleLoginError}
          loginError={loginError}
        />
      )}
    </div>
  );
}
